import { useState } from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Users, Pencil } from 'lucide-react'
import { useBandMembers } from '@/hooks/useBands'
import { useBandContext } from '@/contexts/BandContext'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import type { Band } from '@/types'

const MembersPanel = ({ band }: { band: Band }) => {
  const { members, addMember, removeMember } = useBandMembers(band.id)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('')

  const handleAdd = async () => {
    if (!name.trim() || !email.trim()) return
    try {
      await addMember({ name: name.trim(), email: email.trim(), role: role.trim() })
      setName('')
      setEmail('')
      setRole('')
      toast.success('Member added')
    } catch {
      toast.error('Failed to add member')
    }
  }

  return (
    <div className='mt-4 space-y-3'>
      <h4 className='text-sm font-medium flex items-center gap-1'>
        <Users className='h-4 w-4' /> Members
      </h4>
      {members.map((m) => (
        <div key={m.id} className='flex items-center justify-between rounded border px-3 py-2 text-sm'>
          <div>
            <span className='font-medium'>{m.name}</span>
            <span className='ml-2 text-muted-foreground'>{m.email}</span>
            {m.role && <span className='ml-2 text-xs text-muted-foreground'>({m.role})</span>}
          </div>
          <Button variant='ghost' size='icon' onClick={() => removeMember(m.id)}>
            <Trash2 className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <div className='grid grid-cols-3 gap-2'>
        <Input placeholder='Name' value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder='Email' type='email' value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input placeholder='Role (e.g. Guitar)' value={role} onChange={(e) => setRole(e.target.value)} />
      </div>
      <Button size='sm' onClick={handleAdd} disabled={!name.trim() || !email.trim()}>
        <Plus className='mr-1 h-4 w-4' /> Add Member
      </Button>
    </div>
  )
}

export const BandsPage = () => {
  const { bands, createBand, updateBand, deleteBand } = useBandContext()
  const { user } = useAuth()
  const [newName, setNewName] = useState('')
  const [editBand, setEditBand] = useState<Band | null>(null)
  const [editName, setEditName] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!newName.trim()) return
    try {
      await createBand(newName.trim())
      setNewName('')
      toast.success('Band created')
    } catch {
      toast.error('Failed to create band')
    }
  }

  const handleUpdate = async () => {
    if (!editBand || !editName.trim()) return
    try {
      await updateBand(editBand.id, editName.trim())
      setEditBand(null)
      toast.success('Band updated')
    } catch {
      toast.error('Failed to update band')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteBand(id)
      toast.success('Band deleted')
    } catch {
      toast.error('Failed to delete band')
    }
  }

  return (
    <div className='mx-auto max-w-3xl p-6 space-y-6'>
      <h1 className='text-2xl font-bold'>My Bands</h1>

      <div className='flex gap-2'>
        <Input
          placeholder='New band name'
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
        />
        <Button size='sm' variant='outline' onClick={handleCreate} disabled={!newName.trim()}>
          <Plus className='mr-1 h-3 w-3' /> Create
        </Button>
      </div>

      {bands.map((band) => {
        const isShared = band.user_id !== user?.id
        return (
        <Card key={band.id}>
          <CardHeader className='flex flex-row items-center justify-between'>
            <CardTitle className='text-lg flex items-center gap-2'>
              {band.name}
              {isShared && <Badge variant='secondary'> SHARED </Badge>}
            </CardTitle>
            <div className='flex gap-1'>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => setExpanded(expanded === band.id ? null : band.id)}
              >
                <Users className='h-4 w-4' />
              </Button>
              {!isShared && (
              <>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => {
                  setEditBand(band)
                  setEditName(band.name)
                }}
              >
                <Pencil className='h-4 w-4' />
              </Button>
              <Button variant='ghost' size='icon' onClick={() => handleDelete(band.id)}>
                <Trash2 className='h-4 w-4' />
              </Button>
              </>
              )}
            </div>
          </CardHeader>
          {expanded === band.id && (
            <CardContent>
              <MembersPanel band={band} />
            </CardContent>
          )}
        </Card>
        )
      })}

      <Dialog open={!!editBand} onOpenChange={() => setEditBand(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Band</DialogTitle>
            <DialogDescription>Change the band name.</DialogDescription>
          </DialogHeader>
          <div className='space-y-2'>
            <Label>Name</Label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setEditBand(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
